import { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Sparkles } from 'lucide-react';
import WizardProgress from './WizardProgress';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface WizardStep {
  id: string;
  title: string;
}

interface WizardLayoutProps {
  children: ReactNode;
  steps: WizardStep[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
  onBack?: () => void;
  onNext?: () => void;
  onSkip?: () => void;
  showBack?: boolean;
  showNext?: boolean;
  showSkip?: boolean;
  nextLabel?: string;
  backLabel?: string;
  skipLabel?: string;
  isNextDisabled?: boolean;
  showProgress?: boolean;
  stepHint?: string;
}

const WizardLayout = ({
  children,
  steps,
  currentStep,
  onStepClick,
  onBack,
  onNext,
  onSkip,
  showBack = true,
  showNext = true,
  showSkip = false,
  nextLabel = 'Continuar',
  backLabel = 'Atrás',
  skipLabel = 'Omitir por ahora',
  isNextDisabled = false,
  showProgress = true,
  stepHint,
}: WizardLayoutProps) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blush-light/30 via-background to-sage-light/20 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-blush/10 to-transparent rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
      <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-bl from-sage/10 to-transparent rounded-full translate-x-1/2 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-gradient-to-t from-gold-light/20 to-transparent rounded-full blur-3xl" />
      
      {/* Floating hearts decoration */}
      <div className="absolute top-32 left-[10%] animate-float opacity-20">
        <Heart className="w-6 h-6 text-primary fill-primary" />
      </div>
      <div className="absolute top-48 right-[15%] animate-float opacity-15" style={{ animationDelay: '1s' }}>
        <Sparkles className="w-8 h-8 text-gold" />
      </div>
      <div className="absolute bottom-32 left-[20%] animate-float opacity-15" style={{ animationDelay: '2s' }}>
        <Heart className="w-4 h-4 text-primary fill-primary" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 min-h-screen flex flex-col">
        {/* Header */}
        <header className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Heart className="w-6 h-6 text-primary fill-primary" />
            <span className="text-sm font-medium text-muted-foreground tracking-widest uppercase">
              Tu boda perfecta
            </span>
            <Heart className="w-6 h-6 text-primary fill-primary" />
          </div>
        </header>

        {/* Progress */}
        {showProgress && (
          <div className="mb-8">
            <WizardProgress
              steps={steps}
              currentStep={currentStep}
              onStepClick={onStepClick}
            />
          </div>
        )}

        {/* Step hint */}
        {stepHint && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-sm text-muted-foreground mb-4 max-w-lg mx-auto"
          >
            <span className="inline-flex items-center gap-1.5 bg-muted/50 px-3 py-1.5 rounded-full">
              <Sparkles className="w-3.5 h-3.5 text-gold" />
              {stepHint}
            </span>
          </motion.p>
        )}

        {/* Content */}
        <div className="flex-1 flex flex-col">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="flex-1"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-border/50">
          <div>
            {showBack && currentStep > 0 && (
              <Button
                variant="ghost"
                onClick={onBack}
                className="text-muted-foreground hover:text-foreground"
              >
                ← {backLabel}
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {showSkip && (
              <Button
                variant="ghost"
                onClick={onSkip}
                className="text-muted-foreground text-sm"
              >
                {skipLabel}
              </Button>
            )}
            
            {showNext && (
              <Button
                onClick={onNext}
                disabled={isNextDisabled}
                className={cn(
                  "min-w-[140px] shadow-lg",
                  "bg-gradient-to-r from-primary to-primary/90",
                  "hover:shadow-xl hover:scale-[1.02] transition-all"
                )}
              >
                {nextLabel} →
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardLayout;
